import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StickySocial from '../components/StickySocial';
import {
  Sprout, Sun, Shirt, Droplet, Lock, Link as LinkIcon,
} from 'lucide-react';

const tipps = [
  {
    icon: Sprout,
    text:
      'Die Liegewiese gibt dir beim Eindrehen ein gutes Feedback, was den Halt der Schraube im Boden betrifft. Achte darauf, bündig in eine ausreichend verfestigte Grasnarbe einzudrehen.',
  },
  {
    icon: Sun,
    text:
      'Verwende einen schattigen Platz für den Spinsafe, wenn du dein Smartphone oder andere Elektronik einschließt, um ein unnötiges Aufheizen im direkten Sonnenlicht zu vermeiden.',
  },
  {
    icon: Shirt,
    text:
      'Als zusätzliches Versteck kannst du einfach ein helles Handtuch oder Kleidungsstück über den Spinsafe legen.',
  },
  {
    icon: Droplet,
    text:
      'Sollte der Boden einmal sehr trocken sein, gieße einfach etwas Wasser von oben in die Schraube hinein (z. B. mithilfe der Oberschale). Das Wasser tritt durch die vier dafür vorgesehenen Löcher aus, weicht den Boden ein und dient als Schmiermittel.',
  },
  {
    icon: Lock,
    text:
      'Du kannst zum Verschließen das beiliegende Schloss verwenden. Alternativ funktioniert auch jedes handelsübliche Vorhängeschloss mit einer Bügelstärke von max. 5 mm.',
  },
  {
    icon: LinkIcon,
    text:
      'Hänge zum Transport das Schloss einfach an die Schlossstange. Somit kannst du nichts vergessen oder verlieren.',
  },
];

export default function HowTo() {
  return (
    <>
      <Header />
      <StickySocial />

      {/* HERO / INTRO */}
      <section className="pt-32 pb-12 bg-spin-sand">
        <div className="max-w-4xl mx-auto px-5 text-center">
          <p className="text-sm uppercase tracking-[0.3em] text-spin-green font-semibold mb-3">
            How To
          </p>
          <h1 className="text-4xl md:text-5xl font-heading font-extrabold text-spin-dark">
            Wie funktioniert der Spinsafe
          </h1>
          <hr className="section-divider" />
          <p className="text-spin-grey leading-relaxed max-w-2xl mx-auto text-lg">
            Du hast dir einen Spinsafe gekauft oder planst einen Kauf, und möchtest ganz genau
            wissen, wie alles funktioniert?
          </p>
          <p className="text-spin-dark font-semibold mt-3">
            Dann bist du hier genau richtig!
          </p>
        </div>
      </section>

      {/* VIDEO */}
      <section className="py-16 bg-spin-dark text-white">
        <div className="max-w-5xl mx-auto px-5">
          <div className="relative aspect-video w-full overflow-hidden rounded-lg shadow-2xl bg-black">
            <iframe
              src="https://player.vimeo.com/video/344421378?title=0&byline=0&portrait=0"
              title="Spinsafe Tutorial"
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* APP DOWNLOAD */}
      <section id="apps" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-5">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-spin-dark">
              Die Spinsafe Alarm-App
            </h2>
            <hr className="section-divider" />
            <p className="text-spin-grey leading-relaxed max-w-3xl mx-auto">
              Aktuell überarbeiten wir unsere Alarm-App und bringen sie in eine neue Version.
              Sie nutzt die Sensorik deines Handys, um Gewalteinwirkung auf den Spinsafe
              festzustellen, und gibt bei einem Diebstahlversuch einen schrillen Warnton über
              die Lautsprecher aus. Bleib' auf dem Laufenden und melde dich für den Newsletter
              an, um informiert zu werden sobald die App verfügbar ist.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Android */}
            <div className="text-center bg-spin-sand rounded-lg p-8">
              <div className="flex items-center justify-center gap-3 mb-4">
                <svg viewBox="0 0 24 24" className="w-8 h-8 text-spin-dark" fill="currentColor" aria-hidden="true">
                  <path d="M3.609 1.814 13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893 2.302 2.302-10.937 6.333zm3.71-2.149 2.379 1.378a1 1 0 0 1 0 1.731l-2.379 1.378-2.5-2.244zM5.864 2.658l10.937 6.333-2.302 2.302z" />
                </svg>
                <h3 className="font-heading font-bold text-lg text-spin-dark">Android</h3>
              </div>
              <span className="inline-block px-4 py-2 bg-spin-green/10 text-spin-green text-sm font-bold uppercase tracking-wider rounded">
                Bald verfügbar
              </span>
              <p className="text-xs text-spin-grey mt-3">In Entwicklung</p>
            </div>

            {/* iOS */}
            <div className="text-center bg-spin-sand rounded-lg p-8">
              <div className="flex items-center justify-center gap-3 mb-4">
                <svg viewBox="0 0 24 24" className="w-8 h-8 text-spin-dark" fill="currentColor" aria-hidden="true">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                </svg>
                <h3 className="font-heading font-bold text-lg text-spin-dark">iOS</h3>
              </div>
              <span className="inline-block px-4 py-2 bg-spin-green/10 text-spin-green text-sm font-bold uppercase tracking-wider rounded">
                Bald verfügbar
              </span>
              <p className="text-xs text-spin-grey mt-3">In Entwicklung</p>
            </div>
          </div>
        </div>
      </section>

      {/* TIPPS UND TRICKS */}
      <section className="py-20 bg-spin-sand">
        <div className="max-w-6xl mx-auto px-5">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-spin-dark">
              Tipps und Tricks
            </h2>
            <hr className="section-divider" />
            <p className="text-spin-grey leading-relaxed max-w-3xl mx-auto">
              Damit du noch mehr aus deinem Spinsafe herausholen kannst, haben wir hier ein paar
              Tipps für die optimale Verwendung gesammelt. Hast du noch einen tollen Tipp, den wir
              hier noch nicht aufführen? Schreib uns gerne eine{' '}
              <a href="mailto:info@spinsafe.de" className="text-spin-green hover:underline">
                E-Mail
              </a>
              , wir sind gespannt!
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tipps.map(({ icon: Icon, text }, i) => (
              <div
                key={i}
                className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow flex gap-4"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-md bg-spin-green/10 flex items-center justify-center">
                  <Icon size={22} className="text-spin-green" strokeWidth={2} />
                </div>
                <p className="text-spin-grey text-sm leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
